// StoryCard — Immersive glassmorphism card for Technology Radar overview
import { useLocation } from 'wouter-preact'
import { useRef, useEffect, useState } from 'preact/hooks'

/**
 * Story card with colored glassmorphism gradient and stacked mini-chart previews.
 *
 * @param {Object} props
 * @param {Object} props.section - Section config from radarConfig.js
 * @param {Array|Object|null} props.previewOption - ECharts option(s) for mini-plot previews
 */
export default function StoryCard({ section, previewOption }) {
  const [, setLocation] = useLocation()

  // Normalize to array
  const previewOptions = Array.isArray(previewOption)
    ? previewOption
    : previewOption
      ? [previewOption]
      : []

  return (
    <article
      className="story-card"
      style={{ '--section-accent': section.accentColor }}
      onClick={() => setLocation(section.route)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setLocation(section.route)
        }
      }}
      aria-label={`${section.title} — ${section.teaserQuestion}`}
    >
      <div className="story-card__content">
        <span className="story-card__plot-count">{section.plotCount} Slides</span>
        <h3 className="story-card__title">{section.title}</h3>
        <p className="story-card__teaser">{section.teaserQuestion}</p>
        <p className="story-card__description">{section.description}</p>
      </div>
      {previewOptions.length > 0 ? (
        <div className="story-card__stack">
          {previewOptions.map((opt, i) => (
            <MiniChartCard key={i} option={opt} index={i} total={previewOptions.length} />
          ))}
        </div>
      ) : (
        <div style={{ flex: 1 }} />
      )}
      <div className="story-card__footer">
        <span className="story-card__cta">Explore</span>
        <span className="story-card__arrow" aria-hidden="true">&rarr;</span>
      </div>
    </article>
  )
}

/**
 * A small card with a mini ECharts preview, positioned in a stack.
 */
function MiniChartCard({ option, index, total }) {
  const chartContainerRef = useRef(null)
  const miniCardRef = useRef(null)
  const chartRef = useRef(null)
  const [ECharts, setECharts] = useState(null)
  const [fanHeight, setFanHeight] = useState(180)

  // Measure stack container height for dynamic fan spread
  useEffect(() => {
    const el = miniCardRef.current
    if (!el) return
    const stack = el.parentElement
    if (!stack) return
    const update = () => {
      const h = stack.clientHeight
      // 75% of available space, clamped between 80 and 400
      setFanHeight(Math.round(Math.max(60, Math.min(h * 0.55, 300))))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(stack)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    import('echarts/core').then((mod) => {
      if (!cancelled) setECharts(mod)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!ECharts || !option || !chartContainerRef.current) return

    Promise.all([
      import('echarts/charts'),
      import('echarts/components'),
      import('echarts/renderers'),
    ]).then(([charts, components, renderers]) => {
      ECharts.use([
        charts.LineChart,
        charts.ScatterChart,
        charts.BarChart,
        charts.RadarChart,
        components.GridComponent,
        components.TooltipComponent,
        components.LegendComponent,
        components.RadarComponent,
        renderers.CanvasRenderer,
      ])

      if (chartRef.current) chartRef.current.dispose()

      const instance = ECharts.init(chartContainerRef.current, null, { renderer: 'canvas' })
      chartRef.current = instance

      const tinyLabel = { fontSize: 7, color: 'rgba(255,255,255,0.4)' }
      const miniOption = {
        ...option,
        title: option.title
          ? { ...option.title, top: 3, left: 'center', textStyle: { ...(option.title.textStyle || {}), fontSize: 8, color: 'rgba(255,255,255,0.65)' } }
          : undefined,
        legend: undefined,
        tooltip: { show: false },
        animation: false,
        grid: { top: 22, right: 10, bottom: 22, left: 28, containLabel: false },
        xAxis: option.xAxis
          ? { ...option.xAxis, name: undefined, axisLabel: { ...(option.xAxis.axisLabel || {}), ...tinyLabel }, splitLine: { show: false } }
          : undefined,
        yAxis: option.yAxis
          ? { ...option.yAxis, name: undefined, axisLabel: { ...(option.yAxis.axisLabel || {}), ...tinyLabel }, splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } } }
          : undefined,
        radar: option.radar
          ? {
              ...option.radar,
              center: ['50%', '55%'],
              radius: '55%',
              name: { textStyle: { fontSize: 6, color: 'rgba(255,255,255,0.35)' } },
              splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
              splitArea: { show: false },
              axisLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
            }
          : undefined,
      }

      instance.setOption(miniOption)

      const ro = new ResizeObserver(() => instance.resize())
      ro.observe(chartContainerRef.current)

      return () => {
        ro.disconnect()
        instance.dispose()
      }
    })

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose()
        chartRef.current = null
      }
    }
  }, [ECharts, option])

  // Cards fan symmetrically from center of the free zone
  const maxSpread = total - 1 || 1
  const center = (total - 1) / 2

  // Default: compact stack, slight diagonal peek
  const defX = (index - center) * 2.5
  const defY = (index - center) * -3

  // Hover: spread symmetrically using measured available height
  const totalSpreadX = 20
  const stepX = maxSpread > 0 ? totalSpreadX / maxSpread : 0
  const stepY = maxSpread > 0 ? fanHeight / maxSpread : 0
  const hovX = (index - center) * stepX
  const hovY = (index - center) * -stepY

  const zIndex = total - index

  return (
    <div
      ref={miniCardRef}
      className="story-card__mini-card"
      style={{
        '--x-def': `${defX}%`,
        '--y-def': `${defY}px`,
        '--x-hov': `${hovX}%`,
        '--y-hov': `${hovY}px`,
        transform: `translate(var(--x-def), var(--y-def))`,
        zIndex,
        opacity: index === 0 ? 1 : Math.max(0.4, 0.85 - (index * 0.08)),
      }}
    >
      <div ref={chartContainerRef} className="story-card__mini-chart" />
    </div>
  )
}
